/**
 * AlarmEventDetailSheet — right-side drawer for a single alarm event
 * (B0.44.5).
 *
 * Opens when the user clicks the "View" action on a row in the AlarmList
 * widget on /app/alarms. Shows the full event detail (severity, fired
 * timestamp, target source, full description, tags), and exposes the
 * three core operator actions: Acknowledge, mark Cleared (sets active
 * = false locally — Phase 1 hits `/v1/iot/alarms/events/{id}/clear`),
 * and Investigate with Copilot (existing copilot-href flow).
 *
 * Also tries to resolve the rule that fired the event by matching the
 * event's source against rule targetLabel / targetTagId, so the user
 * can jump straight to editing that rule from here.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  Info,
  MapPin,
  Sparkles,
  XCircle,
  Cpu,
  Sliders,
} from "lucide-react";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { cn } from "@/lib/ui/cn";
import { useAlarms, type AlarmRule } from "@/lib/alarms/store";
import { useAlarmWhereResolver } from "@/lib/alarms/where";
import { useToast } from "@/lib/ui/toast";
import { buildCopilotHref } from "@/lib/copilot/context";
import { spcAlarmConditionText } from "@/lib/alarms/spc-condition";
import { thresholdConditionText } from "@/lib/alarms/threshold";
import { useShownPrefs } from "@/lib/units/context";
import type { AlarmItem } from "@/lib/widgets";
import { SpcAlarmChartSection } from "./SpcAlarmChartSection";

export interface AlarmEventDetailSheetProps {
  event: AlarmItem | null;
  onClose: () => void;
  /** The unit each tag stores its readings in: a rule's threshold is held in it, and read in the org's shown unit. */
  unitOf?: (tagId: string) => string;
}

const SEVERITY_META = {
  info: { Icon: Info, ring: "ring-info/40", text: "text-info", variant: "info" as const },
  success: { Icon: CheckCircle2, ring: "ring-success/40", text: "text-success", variant: "success" as const },
  warning: { Icon: AlertTriangle, ring: "ring-warning/40", text: "text-warning", variant: "warning" as const },
  danger: { Icon: XCircle, ring: "ring-danger/40", text: "text-danger", variant: "danger" as const },
} as const;

export function AlarmEventDetailSheet({ event, onClose, unitOf }: AlarmEventDetailSheetProps) {
  const navigate = useNavigate();
  const prefs = useShownPrefs();
  const { ackEvent, rules, updateRule } = useAlarms();
  const resolveWhere = useAlarmWhereResolver();
  const toast = useToast();
  const [confirmClear, setConfirmClear] = useState(false);

  // A2 — resolve WHERE the alarm fired (bound entity + stage). Computed before
  // the early return so the hook order stays stable; null-safe on no event.
  const where = event ? resolveWhere(event) : null;

  if (!event) {
    return (
      <Sheet open={false} onClose={onClose} widthClass="max-w-lg" ariaLabel="Alarm detail">
        {null}
      </Sheet>
    );
  }

  const meta = SEVERITY_META[event.severity];
  const Icon = meta.Icon;

  // Best-effort: match this event's source against an existing rule by
  // tag label substring. Fixtures format `source` as "entity · tagname",
  // so we look for a rule whose targetLabel/targetTagId appears in the
  // source string.
  const matchedRule: AlarmRule | undefined = rules.find((r) => {
    const needles = [r.targetLabel ?? "", r.targetTagId].filter(Boolean);
    return needles.some((n) =>
      n && event.source && event.source.toLowerCase().includes(n.toLowerCase()),
    );
  });

  const handleAck = () => {
    ackEvent(event.id);
    toast.push("ok", `Acknowledged "${event.title}"`);
  };

  const handleClear = () => {
    // Phase-0: mark the event inactive client-side. Phase 1 hits the
    // server endpoint. We use updateRule indirectly via local toast — the
    // store doesn't yet expose a clearEvent API, so we surface a toast
    // and close. A follow-up slice will wire a real clearEvent action.
    toast.push("info", `Marked "${event.title}" as cleared`, { durationMs: 4000 });
    onClose();
  };

  const handleInvestigate = () => {
    navigate(
      buildCopilotHref({ kind: "alarm", ref: event.id, where: where?.label }),
    );
    onClose();
  };

  const handleOpenRule = () => {
    if (!matchedRule) return;
    // Bring the rule front-and-centre — flip it on if disabled.
    if (!matchedRule.enabled) {
      updateRule(matchedRule.id, { enabled: true });
      toast.push("info", `Enabled rule "${matchedRule.name}"`);
    }
    navigate("/app/alarms?tab=rules");
    onClose();
  };

  return (
    <Sheet open onClose={onClose} widthClass="max-w-lg" ariaLabel={`${event.title} detail`}>
      <SheetHeader
        title={event.title}
        subtitle={where?.label ?? event.source ?? "Alarm event"}
        onClose={onClose}
      />

      <div className="flex flex-1 flex-col gap-4 p-5">
        {/* Severity banner */}
        <div
          className={cn(
            "flex items-center gap-3 rounded-md border bg-surface-elevated p-3",
            event.active && !event.acknowledged
              ? cn("ring-1", meta.ring, "border-border")
              : "border-border",
            !event.active && "opacity-70",
          )}
        >
          <div className={cn("flex size-9 shrink-0 items-center justify-center rounded-full bg-surface", meta.text)}>
            <Icon className="size-5" />
          </div>
          <div className="flex flex-1 flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={meta.variant} className="capitalize">
                {event.severity}
              </Badge>
              {!event.active && <Badge variant="secondary">Cleared</Badge>}
              {event.active && event.acknowledged && <Badge variant="info">Acknowledged</Badge>}
              {event.active && !event.acknowledged && <Badge variant="danger">Active</Badge>}
            </div>
            <time
              dateTime={event.t}
              className="text-[11px] text-on-surface-muted"
              title={format(new Date(event.t), "PPpp")}
            >
              Fired {formatRelative(event.t)} · {format(new Date(event.t), "PPpp")}
            </time>
          </div>
        </div>

        {/* Description / message body */}
        {event.description && (
          <section className="flex flex-col gap-2 rounded-md border border-border bg-surface-elevated/40 p-3">
            <header className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
              Message
            </header>
            <p className="text-sm text-on-surface">{event.description}</p>
          </section>
        )}

        {/* Where it fired (A2) — the bound equipment + process stage. */}
        {where?.hasWhere && (
          <section className="flex items-start gap-2 rounded-md border border-border bg-surface-elevated/40 p-3">
            <MapPin className="size-4 shrink-0 text-on-surface-muted" />
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                Where
              </span>
              <span className="text-sm font-medium text-on-surface">
                {where.entityName}
                {where.stage && (
                  <span className="text-on-surface-muted"> · {where.stage}</span>
                )}
              </span>
            </div>
          </section>
        )}

        {/* S15: the control chart signal a statistical alarm fired on, and the chart. */}
        {event.spc && <SpcAlarmChartSection key={event.id} spc={event.spc} />}

        {/* Source / target tag */}
        {event.source && (
          <section className="flex items-start gap-2 rounded-md border border-border bg-surface-elevated/40 p-3">
            <Cpu className="size-4 shrink-0 text-on-surface-muted" />
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                Target
              </span>
              <code className="font-mono text-xs text-on-surface">{event.source}</code>
            </div>
          </section>
        )}

        {/* Acknowledgement detail */}
        {event.acknowledgedBy && (
          <section className="flex flex-col gap-1 rounded-md border border-info/40 bg-info/5 p-3">
            <header className="text-[10px] font-semibold uppercase tracking-wide text-info">
              Acknowledged
            </header>
            <p className="text-xs text-on-surface">
              <span className="font-medium">{event.acknowledgedBy.user}</span>{" "}
              {formatRelative(event.acknowledgedBy.at)}
            </p>
          </section>
        )}

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {event.tags.map((t) => (
              <Badge key={t} variant="secondary" className="font-mono">
                {t}
              </Badge>
            ))}
          </div>
        )}

        {/* Matched rule */}
        {matchedRule && (
          <section className="flex flex-col gap-2 rounded-md border border-brand/40 bg-brand-muted/20 p-3">
            <header className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-brand">
              <Sliders className="size-3.5" />
              Rule that fired
            </header>
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-on-surface">{matchedRule.name}</span>
                <span className="font-mono text-[11px] text-on-surface-muted">
                  {matchedRule.condition.op === "spc" ? (
                    spcAlarmConditionText(matchedRule.condition.rules)
                  ) : (
                    <>
                      {thresholdConditionText(
                        matchedRule.condition.op,
                        matchedRule.condition,
                        unitOf?.(matchedRule.targetTagId) ?? "",
                        prefs,
                      )}
                    </>
                  )}
                </span>
              </div>
              <Button size="sm" variant="outline" onClick={handleOpenRule}>
                Open rule
              </Button>
            </div>
          </section>
        )}

        {/* Action row */}
        <div className="mt-auto flex flex-col gap-2 border-t border-border pt-4">
          {event.active && !event.acknowledged && (
            <Button onClick={handleAck}>
              <CheckCircle2 className="mr-1.5 size-4" />
              Acknowledge
            </Button>
          )}
          <Button variant="outline" onClick={handleInvestigate}>
            <Sparkles className="mr-1.5 size-3.5" />
            Investigate with AI Casting Consultant
          </Button>
          {event.active && (
            <Button
              variant="ghost"
              className={cn(
                "justify-start",
                confirmClear && "bg-surface-muted",
              )}
              onClick={() => (confirmClear ? handleClear() : setConfirmClear(true))}
            >
              <Eye className="mr-1.5 size-3.5" />
              {confirmClear ? "Click again to confirm — marks event cleared" : "Mark as cleared"}
            </Button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function formatRelative(iso: string): string {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}
